import { fetchAuthSession } from 'aws-amplify/auth';
import { API_CONFIG, getApiUrl } from '../config/apiConfig';
import { APIError, ApiError } from '../types/api';
import logger from '../utils/logger';

export interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
  skipAuth?: boolean;
}

class HttpClient {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    // テスト環境での認証スキップ
    if (process.env.REACT_APP_SKIP_AUTH === 'true') {
      logger.info('🔓 [httpClient] Skipping auth in test mode');
      return {};
    }
    
    try {
      // 最大3回リトライして認証セッションを取得 (amplify v6 fetchAuthSession)
      let token: string | undefined;
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          logger.debug(`🔒 [httpClient] Auth session attempt ${retryCount + 1}/${maxRetries}`);
          const session = await fetchAuthSession();
          token = session.tokens?.idToken?.toString();

          if (token) {
            logger.debug('✅ [httpClient] Session obtained successfully');
            break;
          }
          logger.warn('Session has no idToken, retrying...');
          throw new Error('No idToken in session');
        } catch (sessionError) {
          logger.error(`Session attempt ${retryCount + 1} failed:`, sessionError);
          retryCount++;

          if (retryCount < maxRetries) {
            // Wait before retry (exponential backoff)
            const delay = 100 * Math.pow(2, retryCount);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      if (!token) {
        throw new Error('Failed to obtain valid session after retries');
      }

      // API Gateway Cognito Authorizerには IDToken が必要
      return {
        'Authorization': `Bearer ${token}`,
      };
    } catch (error) {
      logger.error('❌ [httpClient] 認証ヘッダー取得エラー:', error);
      throw new APIError({
        code: 'AUTH_ERROR',
        message: '認証に失敗しました。再度ログインしてください。',
      });
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');

    if (!response.ok) {
      let errorData: ApiError;
      
      if (isJson) {
        const errorResponse = await response.json();
        errorData = errorResponse.error || {
          code: 'HTTP_ERROR',
          message: `HTTPエラー: ${response.status} ${response.statusText}`,
        };
      } else {
        errorData = {
          code: 'HTTP_ERROR',
          message: `HTTPエラー: ${response.status} ${response.statusText}`,
        };
      }

      // 認証エラーの場合は特別な処理
      if (response.status === 401) {
        errorData.code = 'UNAUTHORIZED';
        errorData.message = '認証が必要です。再度ログインしてください。';
      } else if (response.status === 403) {
        errorData.code = 'FORBIDDEN';
        errorData.message = 'このリソースにアクセスする権限がありません。';
      } else if (response.status === 404) {
        errorData.code = 'NOT_FOUND';
        errorData.message = '要求されたリソースが見つかりません。';
      } else if (response.status >= 500) {
        errorData.code = 'SERVER_ERROR';
        errorData.message = 'サーバーエラーが発生しました。しばらく後に再試行してください。';
      }

      throw new APIError(errorData);
    }

    if (isJson) {
      return await response.json();
    }

    return {} as T;
  }

  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const {
      method = 'GET',
      headers = {},
      body,
      timeout = API_CONFIG.timeout,
      skipAuth = false,
    } = config;

    try {
      const authHeaders = skipAuth ? {} : await this.getAuthHeaders();
      const url = getApiUrl(endpoint);

      logger.debug('🔍 [httpClient] Request URL:', url);
      logger.debug('🔍 [httpClient] Auth headers:', authHeaders);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const requestHeaders = {
        ...API_CONFIG.headers,
        ...authHeaders,
        ...headers,
      };

      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return await this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new APIError({
            code: 'TIMEOUT_ERROR',
            message: 'リクエストがタイムアウトしました。ネットワーク接続を確認してください。',
          });
        }

        if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
          throw new APIError({
            code: 'NETWORK_ERROR',
            message: 'ネットワークエラーが発生しました。インターネット接続を確認してください。',
          });
        }
      }

      throw new APIError({
        code: 'UNKNOWN_ERROR',
        message: '予期しないエラーが発生しました。',
      });
    }
  }

  async get<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', headers });
  }

  async post<T>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body, headers });
  }

  async put<T>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body, headers });
  }

  async delete<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', headers });
  }
}

export const httpClient = new HttpClient();